/**
 * CR431: Surface → View Registry
 * 
 * Maps each UI surface to its canonical Supabase view, environment,
 * and default select expression. Prevents view drift across surfaces.
 */

export type DealSurface =
  | "public_grid"
  | "public_details"
  | "live_preview"
  | "deal_card_editor"
  | "layout_studio";

export type DealEnv = "public" | "live" | "sample";

export interface DealViewConfig {
  env: DealEnv;
  viewName: string;
  select: string;
  idColumn: "id" | "deal_id";
}

export const DEAL_VIEW_REGISTRY: Record<DealSurface, DealViewConfig> = {
  public_grid: {
    env: "public",
    viewName: "v_public_active_deals",
    select: "*",
    idColumn: "id",
  },
  public_details: {
    env: "public",
    viewName: "v_public_active_deals",
    select: "*",
    idColumn: "id",
  },
  live_preview: {
    env: "live",
    viewName: "v_deals_live_enriched",
    select: "*",
    idColumn: "id",
  },
  deal_card_editor: {
    env: "sample",
    viewName: "v_deal_card_editor_sample_deals",
    select: "*",
    idColumn: "id",
  },
  layout_studio: {
    env: "sample",
    viewName: "v_layout_studio_sample_deals",
    select: "*",
    idColumn: "id",
  },
};

/**
 * Tag a deal payload with provenance metadata for debugging.
 */
export function tagDealProvenance<T extends Record<string, any>>(
  deal: T,
  surface: DealSurface,
): T & { __surface: DealSurface; __source_view: string; __source_env: DealEnv } {
  const cfg = DEAL_VIEW_REGISTRY[surface];
  return {
    ...deal,
    __surface: surface,
    __source_view: cfg.viewName,
    __source_env: cfg.env,
  };
}
